export type CarCommand = 'next' | 'prev';

/**
 * How a clip stopped: it played out, a steering-wheel button cut it short, the
 * session was paused (button, phone call, another app taking audio) or stopped,
 * or the session interrupted it itself (e.g. the clip it was waiting for is ready).
 */
export type PlayOutcome = 'ended' | CarCommand | 'paused' | 'stopped' | 'interrupted';

export interface PlayOptions {
    rate?: number;
    loop?: boolean;
}

/**
 * Plays every Car mode clip — speech, earcons, waits — through one <audio>
 * element. A page only receives the media keys (next/previous track) while its
 * media element is playing, so the session never leaves it idle.
 */
export class CarPlayer {
    private readonly audio = new Audio();
    private settle: ((outcome: PlayOutcome) => void) | undefined;
    private resumeWaiters: (() => void)[] = [];
    private pausedState = false;
    private stoppedState = false;

    constructor(private readonly onPausedChange: (paused: boolean) => void) {
        this.audio.preload = 'auto';
        this.audio.addEventListener('ended', () => this.finish('ended'));
        this.audio.addEventListener('error', () => this.finish('ended'));
        this.audio.addEventListener('pause', () => {
            // The OS paused us (a call, a navigation prompt that does not duck, Siri…).
            // The event is async: a pause of our own has already settled the clip, or the
            // next clip is playing by now, so neither reaches this branch.
            if (this.audio.paused && !this.audio.ended && this.settle !== undefined)
                this.pause();
        });
        this.setupMediaSession();
    }

    get paused(): boolean {
        return this.pausedState;
    }

    get stopped(): boolean {
        return this.stoppedState;
    }

    play(url: string, options: PlayOptions = {}): Promise<PlayOutcome> {
        if (this.stoppedState)
            return Promise.resolve('stopped');
        if (this.pausedState)
            return Promise.resolve('paused');

        this.finish('interrupted');

        return new Promise<PlayOutcome>(resolve => {
            this.settle = resolve;
            this.audio.loop = options.loop ?? false;
            this.audio.src = url;
            this.audio.playbackRate = options.rate ?? 1;
            this.audio.play().catch(() => {
                // Autoplay refused (no user gesture yet) or the source failed: treat as a pause,
                // the on-screen Play button resumes it.
                if (this.settle === resolve)
                    this.pause();
            });
        });
    }

    command(command: CarCommand): void {
        if (this.stoppedState)
            return;
        if (this.pausedState) {
            this.resume();
            return;
        }
        this.finish(command);
    }

    /** Cuts the current clip short with 'interrupted'. */
    interrupt(): void {
        this.finish('interrupted');
    }

    pause(): void {
        if (this.pausedState || this.stoppedState)
            return;
        this.pausedState = true;
        this.setPlaybackState('paused');
        this.onPausedChange(true);
        this.finish('paused');
    }

    resume(): void {
        if (!this.pausedState || this.stoppedState)
            return;
        this.pausedState = false;
        this.setPlaybackState('playing');
        this.onPausedChange(false);
        const waiters = this.resumeWaiters;
        this.resumeWaiters = [];
        waiters.forEach(waiter => waiter());
    }

    togglePause(): void {
        if (this.pausedState)
            this.resume();
        else
            this.pause();
    }

    /** Resolves once the session is resumed (immediately if it is not paused). */
    waitForResume(): Promise<void> {
        if (!this.pausedState || this.stoppedState)
            return Promise.resolve();
        return new Promise(resolve => this.resumeWaiters.push(resolve));
    }

    stop(): void {
        if (this.stoppedState)
            return;
        this.stoppedState = true;
        this.finish('stopped');
        const waiters = this.resumeWaiters;
        this.resumeWaiters = [];
        waiters.forEach(waiter => waiter());
        this.clearMediaSession();
    }

    setTitle(title: string, subtitle: string): void {
        const session = navigator.mediaSession;
        if (session === undefined || typeof MediaMetadata === 'undefined')
            return;
        session.metadata = new MediaMetadata({ title, artist: subtitle, album: 'Memkeep · Car' });
    }

    private finish(outcome: PlayOutcome): void {
        const settle = this.settle;
        if (settle === undefined)
            return;
        this.settle = undefined;

        if (outcome !== 'ended')
            this.audio.pause();
        settle(outcome);
    }

    private setPlaybackState(state: MediaSessionPlaybackState): void {
        if (navigator.mediaSession !== undefined)
            navigator.mediaSession.playbackState = state;
    }

    private setupMediaSession(): void {
        const session = navigator.mediaSession;
        if (session === undefined)
            return;

        const handlers: [MediaSessionAction, () => void][] = [
            ['nexttrack', () => this.command('next')],
            ['previoustrack', () => this.command('prev')],
            // Some head units send seek instead of track skips.
            ['seekforward', () => this.command('next')],
            ['seekbackward', () => this.command('prev')],
            ['play', () => this.resume()],
            ['pause', () => this.pause()],
            ['stop', () => this.pause()],
        ];
        for (const [action, handler] of handlers) {
            try {
                session.setActionHandler(action, handler);
            } catch {
                // The browser does not know this action.
            }
        }
        session.playbackState = 'playing';
    }

    private clearMediaSession(): void {
        const session = navigator.mediaSession;
        if (session === undefined)
            return;

        const actions: MediaSessionAction[] = ['nexttrack', 'previoustrack', 'seekforward', 'seekbackward', 'play', 'pause', 'stop'];
        for (const action of actions) {
            try {
                session.setActionHandler(action, null);
            } catch {
                // The browser does not know this action.
            }
        }
        session.metadata = null;
        session.playbackState = 'none';
    }
}
